import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { ProtectedDialogContent } from "@/components/ui/ProtectedDialogContent";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import {
  SheetCloseButton,
  SHEET_HEADER_CLASS,
  SHEET_FOOTER_CLASS,
} from "@/components/ui/sheetChrome";
import { SheetMoreMenu } from "@/components/ui/SheetMoreMenu";
import { WizardStepperHeader, StepperNode } from "./WizardStepperHeader";

interface TaskFormSheetShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Whether the form has any unsaved input; controls X-close confirmation. */
  isDirty: boolean;
  /** 4 visual macro-step nodes (or fewer if some are filtered). */
  nodes: StepperNode[];
  currentIndex: number;
  reachedIndex: number;
  onJump: (i: number) => void;
  onBack: () => void;
  onContinue: () => void;
  /** Whether Continue should be disabled (e.g. required field empty). */
  continueDisabled?: boolean;
  /** True when on the last step — replaces "Continuar" with the submit button. */
  isLastStep: boolean;
  /** The actual submit button (label/loading/handler) for the final step. */
  submitButton: React.ReactNode;
  /** Body content for the active macro step. */
  children: React.ReactNode;
  /** Optional sticky element rendered above the footer (e.g. location summary). */
  stickyAboveFooter?: React.ReactNode;
  /**
   * Las líneas del «⋯» a la izquierda de la X (26-09-2026). Crear tarea NO usa
   * FormSheet, así que el hueco de FormSheet no llegaba aquí: este es el mismo,
   * con el mismo `SheetMoreMenu`. Sin la prop no se pinta nada.
   */
  trailingMenu?: React.ReactNode;
}

export function TaskFormSheetShell({
  open,
  onOpenChange,
  title,
  isDirty,
  nodes,
  currentIndex,
  reachedIndex,
  onJump,
  onBack,
  onContinue,
  continueDisabled,
  isLastStep,
  submitButton,
  children,
  stickyAboveFooter,
  trailingMenu,
}: TaskFormSheetShellProps) {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const bodyRef = React.useRef<HTMLDivElement>(null);

  // Each macro-step should open at the top, not wherever the previous step was
  // scrolled to (otherwise e.g. the Schedule step hides the date field).
  React.useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [currentIndex]);

  const handleClose = () => {
    if (isDirty) setConfirmOpen(true);
    else onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => (!o ? handleClose() : onOpenChange(true))}>
        <ProtectedDialogContent
          isDirty={isDirty}
          /* A pantalla completa en el teléfono, siempre. Es un asistente de
             cuatro pasos: el primero son dos campos y el tercero una tabla de
             productos, así que midiendo el contenido cambiaría de tamaño en cada
             paso y parecería que se reabre. Lo que importa aquí no es la
             longitud, es que NO se mueva. */
          fit="expanded"
          hideCloseButton
          onDiscard={() => onOpenChange(false)}
          className={cn(
            // Colocarla abajo, el redondeo y la subida ya los pone
            // ProtectedDialogContent; aquí solo el relleno propio y el ancho de
            // la ventana de escritorio.
            "!p-0 !gap-0 flex flex-col bg-white h-auto",
            "overflow-hidden overscroll-contain",
            "md:max-w-[520px] lg:max-w-[560px] md:w-[calc(100vw-3rem)] md:h-auto md:max-h-[85vh]",
          )}
        >
          {/* Header — `pt-7` en el teléfono para dejar sitio al tirador, que va
              en absoluto por encima. */}
          <div className={cn(SHEET_HEADER_CLASS, "pb-3 shrink-0 max-md:pt-7")}>
            <div className="flex items-start justify-between gap-3 mb-5">
              <div className="min-w-0">
                {/* Sin «Paso N de M»: el propio indicador de pasos que va justo
                    debajo ya lo dice, y en color. */}
                <h2 className="text-2xl font-bold tracking-tight text-foreground">
                  {title}
                </h2>
              </div>
              {trailingMenu ? (
                <div className="flex items-center gap-1.5 shrink-0">
                  <SheetMoreMenu testId="task-form-more-menu">{trailingMenu}</SheetMoreMenu>
                  <SheetCloseButton onClick={handleClose} aria-label={t("common.close")} />
                </div>
              ) : (
                <SheetCloseButton onClick={handleClose} aria-label={t("common.close")} />
              )}
            </div>

            <WizardStepperHeader
              nodes={nodes}
              currentIndex={currentIndex}
              reachedIndex={reachedIndex}
              onJump={onJump}
            />
          </div>

          {/* Scrollable body */}
          <div
            ref={bodyRef}
            /* `pb-8`: con `pb-4` el último campo quedaba pegado al pie fijo y su
               desplegable se veía cortado al llegar abajo del todo. */
            className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain px-5 pb-8"
            style={{ touchAction: "pan-y" }}
          >
            {children}
          </div>

          {/* Sticky above footer (e.g. location summary) */}
          {stickyAboveFooter && (
            <div className="px-5 pt-2 pb-2 shrink-0 bg-gradient-to-t from-white to-white/0">
              {stickyAboveFooter}
            </div>
          )}

          {/* Sticky footer */}
          <div
            className={cn(SHEET_FOOTER_CLASS, "shrink-0 border-t bg-white flex items-center gap-3")}
          >
            {currentIndex > 0 ? (
              <Button
                type="button"
                variant="outline"
                onClick={onBack}
                className="h-12 px-5 rounded-2xl border-[#b6dded] flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                {t("common.back")}
              </Button>
            ) : null}

            {isLastStep ? (
              <div className="flex-1">{submitButton}</div>
            ) : (
              <Button
                type="button"
                onClick={onContinue}
                disabled={continueDisabled}
                className={cn(
                  "flex-1 h-12 rounded-2xl font-semibold text-white",
                  "bg-[#2E84DE]",
                  "hover:bg-[#2E84DE]/90 shadow-[0_10px_24px_-10px_rgba(46,132,222,0.6)]",
                  "disabled:opacity-50 disabled:bg-muted disabled:bg-none disabled:text-muted-foreground disabled:shadow-none",
                )}
              >
                {t("common.wizard.continue")}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </ProtectedDialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("common.discardChangesTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("common.discardChangesDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.keepEditing")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false);
                onOpenChange(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("common.discardConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
